import React, { useState, useMemo, useEffect } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  CalendarDays, Check, ShoppingCart, Trash2, ChevronRight,
  Search, X, PlusCircle, Edit3, ArrowLeft, Image as ImageIcon
} from 'lucide-react';
import StepIndicator from './StepIndicator';
import SingleMealSelection from './SingleMealSelection';

const MealSelectionStyles = () => (
  <style>{`
    :root {
      --primary: #2F7D5E;
      --primary-dark: #245F48;
      --accent: #f4f4f4;
      --border: #e8e8e8;
      --text-main: #1a1a1a;
      --text-sub: #666;
      --danger: #e53e3e;
      --success: #166534;
    }

    .meal-selection-page {
      display: grid;
      grid-template-columns: 1fr 380px;
      gap: 30px;
      max-width: 1200px;
      margin: 0 auto;
      position: relative;
      font-family: 'Plus Jakarta Sans', 'Noto Sans TC', sans-serif;
    }

    /* 左側 Slots 樣式 */
    .slots-container { display: flex; flex-direction: column; gap: 20px; }
    .date-slot-card { background: #fff; border: 1px solid var(--border); border-radius: 4px; padding: 20px; box-shadow: 0 4px 12px rgba(0,0,0,0.03); transition: all 0.3s ease; }
    .date-slot-card:hover { box-shadow: 0 6px 16px rgba(0,0,0,0.06); }
    .date-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 15px; padding-bottom: 10px; border-bottom: 1px solid #f5f5f5; }
    .date-title { font-size: 1.1rem; font-weight: 400; color: var(--primary); display: flex; align-items: center; gap: 8px; }
    .slots-row { display: grid; grid-template-columns: 1fr 1fr; gap: 15px; }

    .meal-slot-btn { display: flex; flex-direction: column; border-radius: 4px; cursor: pointer; transition: all 0.2s ease; position: relative; min-height: 110px; width: 100%; box-sizing: border-box; overflow: hidden; }
    .meal-slot-btn.empty { background: #fdfdfd; border: 1px solid #eee; align-items: center; justify-content: center; color: #888; }
    .meal-slot-btn.empty:hover { background: #fff; border-color: #ccc; transform: translateY(-2px); box-shadow: 0 4px 10px rgba(0,0,0,0.05); color: var(--primary); }
    .meal-slot-btn.filled { background: #fff; border: 1.5px solid var(--primary); padding: 12px; align-items: flex-start; text-align: left; box-shadow: 0 2px 8px rgba(47,125,94,0.06); }
    .meal-slot-btn.filled:hover { background: #fcfcfc; }

    .slot-filled-wrapper { display: flex; gap: 12px; width: 100%; height: 100%; align-items: center; }
    .slot-thumbnail { width: 80px; height: 80px; border-radius: 4px; object-fit: cover; flex-shrink: 0; }
    .slot-thumbnail-placeholder { width: 80px; height: 80px; border-radius: 4px; background: #f5f5f5; display: flex; align-items: center; justify-content: center; color: #ccc; flex-shrink: 0; }
    .slot-content-area { flex: 1; min-width: 0; display: flex; flex-direction: column; }

    .slot-tag { font-size: 0.9rem; font-weight: 400; color: var(--primary); margin-bottom: 2px; display: block; }
    .tag-lunch { color: #c2410c; }
    .tag-dinner { color: #1e40af; }

    .selected-dish-name { font-weight: 400; font-size: 0.9rem; color: var(--text-main); line-height: 1.35; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; }
    .selected-dish-mods { font-size: 0.78rem; color: var(--text-sub); margin-top: 2px; line-height: 1.3; }

    .slot-actions { position: absolute; top: 8px; right: 8px; display: flex; gap: 6px; opacity: 0.8; transition: opacity 0.2s; background: rgba(255,255,255,0.9); border-radius: 20px; padding: 2px; }
    .meal-slot-btn:hover .slot-actions { opacity: 1; }
    .action-btn { width: 26px; height: 26px; border-radius: 50%; background: #fff; border: 1px solid #eee; display: flex; align-items: center; justify-content: center; color: #666; transition: all 0.2s; }
    .action-btn:hover { border-color: #ccc; color: var(--primary); background: #f9f9f9; }
    .action-btn.delete:hover { border-color: #ffcccc; color: var(--danger); background: #fff0f0; }
    .add-meal-action { display: flex; flex-direction: column; align-items: center; gap: 8px; font-size: 0.95rem; font-weight: 400; }
    .add-icon-circle { width: 36px; height: 36px; border-radius: 50%; background: #f4f4f4; display: flex; align-items: center; justify-content: center; color: #666; transition: all 0.2s; }
    .meal-slot-btn.empty:hover .add-icon-circle { background: var(--primary); color: #fff; }

    /* =========================================
       ✅ 專業收據風格訂單摘要 (Professional Receipt Style)
       ========================================= */
    .order-summary {
      position: sticky;
      top: 20px;
      border: 1px solid #e2e8f0;
      border-radius: 4px;
      background: #fff;
      height: fit-content;
      box-shadow: 0 4px 20px rgba(0,0,0,0.04);
      overflow: hidden;
      display: flex;
      flex-direction: column;
    }

    .summary-header {
      padding: 10px 8px;
      background: #fff;
      border-bottom: 1px solid #f1f5f9;
      font-size: 1.1rem;
      font-weight: 400;
      display: flex;
      align-items: center;
      gap: 10px;
      color: var(--primary);
    }

    .summary-scroll {
      padding: 0;
      max-height: calc(100vh - 350px);
      overflow-y: auto;
    }

    /* 日期區塊 */
    .summary-day-block {
      padding: 12px 8px;
      border-bottom: 1px solid #f1f5f9;
    }
    .summary-day-block:last-child { border-bottom: none; }

    .summary-date-label {
      font-size: 0.85rem;
      font-weight: 400;
      color: #1a1a1a;
      margin-bottom: 8px;
      padding-bottom: 4px;
      border-bottom: 1px solid #f1f5f9;
    }

    /* 項目網格佈局 [圖片] [內容區] */
    .summary-item-row {
      display: grid;
      grid-template-columns: 48px 1fr;
      gap: 10px;
      margin-bottom: 16px;
      align-items: start;
    }
    .summary-item-row:last-child { margin-bottom: 0; }

    .summary-img {
      width: 48px;
      height: 48px;
      border-radius: 4px;
      object-fit: cover;
      background: #f1f5f9;
      border: 1px solid #e2e8f0;
    }

    .summary-info-col {
      display: flex;
      flex-direction: column;
      min-width: 0;
      width: 100%;
    }

    /* Tag 樣式 */
    .summary-tag-label {
      display: block;
      font-size: 0.9rem;
      font-weight: 400;
      text-transform: uppercase;
      margin-bottom: 2px;
      letter-spacing: 0.3px;
    }
    .label-lunch { color: #d97706; }
    .label-dinner { color: #2563eb; }

    .dish-title-row {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      gap: 8px;
      margin-bottom: 2px;
    }

    .summary-dish-title {
      font-size: 0.9rem;
      font-weight: 400;
      color: #1e293b;
      line-height: 1.35;
    }

    .summary-dish-price {
      font-size: 0.9rem;
      font-weight: 400;
      color: #1e293b;
      white-space: nowrap;
      text-align: right;
    }

    .summary-mods-container {
      display: flex;
      flex-direction: column;
      gap: 2px;
      width: 100%;
    }

    .mod-line {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      font-size: 0.8rem;
      color: #64748b;
      width: 100%;
      line-height: 1.4;
    }

    .mod-name {
      padding-right: 12px;
      flex: 1;
      text-align: left;
    }

    .mod-price {
      white-space: nowrap;
      text-align: right;
      color: #64748b;
      font-weight: 400;
      font-size: 0.8rem;
    }

    /* 底部結算區 */
    .summary-total-section {
      padding: 12px 8px;
      border-top: 1px solid #e2e8f0;
      background: #fff;
    }

    .bill-row {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 10px;
      font-size: 0.9rem;
      font-weight: 400;
      color: #334155;
    }

    .bill-row.fee-row {
      color: #334155;
      font-weight: normal;
      margin-bottom: 12px;
      padding-bottom: 12px;
      border-bottom: 1px dashed #e2e8f0;
    }

    .bill-row.tax-row { font-size: 0.85rem; color: #94a3b8; }

    .bill-total {
      display: flex;
      justify-content: space-between;
      align-items: flex-end;
      margin-top: 12px;
      padding-top: 16px;
      border-top: 1px solid #e2e8f0;
    }
    .total-text { font-size: 1.1rem; font-weight: 400; color: var(--primary); }
    .total-num { font-size: 1.5rem; font-weight: 400; color: var(--primary); }

    .summary-actions { margin-top: 24px; display: flex; flex-direction: column; gap: 12px; }

    /* Button styles */
    .next-step-btn { width: 100%; padding: clamp(14px, 3vw, 16px) clamp(30px, 5vw, 40px); border-radius: 4px; background: #2F7D5E; color: #fff; border: 1px solid #2F7D5E; font-weight: 400; font-size: 16px; cursor: pointer; display: flex; justify-content: center; align-items: center; gap: 10px; transition: all 0.3s ease; box-shadow: 0 4px 12px rgba(47,125,94,0.2); letter-spacing: 0.3px; }
    .next-step-btn:active { transform: scale(0.98); }
    .next-step-btn:disabled { background: #e8e8e8; cursor: not-allowed; color: #aaa; box-shadow: none; transform: none; border-color: #e8e8e8; }
    .prev-step-btn { width: 100%; padding: clamp(14px, 3vw, 16px) clamp(30px, 5vw, 40px); border-radius: 4px; background: #fff; color: #2F7D5E; border: 1px solid #e0e0e0; font-weight: 400; font-size: 16px; cursor: pointer; display: flex; justify-content: center; align-items: center; gap: 10px; transition: all 0.3s ease; letter-spacing: 0.3px; }
    .prev-step-btn:active { transform: scale(0.98); }

    /* Modal (保持原本樣式) */
    .menu-modal-overlay { position: fixed; top: 0; left: 0; right: 0; bottom: 0; background: #fff; z-index: 2000; animation: fadeIn 0.2s ease; overflow: hidden; }
    @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
    .menu-modal { width: 100%; height: 100%; display: flex; flex-direction: column; background: #fff; overflow: hidden; }
    .modal-header { padding: 16px 32px; border-bottom: 1px solid var(--border); display: flex; align-items: center; justify-content: space-between; background: #fff; gap: 20px; box-shadow: 0 2px 10px rgba(0,0,0,0.02); z-index: 10; }
    .modal-title-area { display: flex; align-items: center; gap: 12px; }
    .modal-title { font-size: 1.4rem; font-weight: 400; color: var(--primary); }
    .back-btn { background: none; border: none; cursor: pointer; display: flex; align-items: center; gap: 6px; font-weight: 400; color: var(--text-sub); padding: 8px 12px; border-radius: 4px; transition: background 0.2s; font-size: 1rem; }
    .back-btn:hover { background: #f0f0f0; color: var(--primary); }
    .search-bar-wrapper { position: relative; flex: 1; max-width: 500px; }
    .search-input { width: 100%; padding: 14px 16px 14px 46px; border-radius: 4px; border: 1px solid #ddd; font-size: 1rem; background: #f9f9f9; transition: all 0.2s; box-sizing: border-box; }
    .search-input:focus { outline: none; border-color: var(--primary); background: #fff; box-shadow: 0 0 0 3px rgba(47,125,94,0.15); }
    .search-icon { position: absolute; left: 18px; top: 50%; transform: translateY(-50%); color: #999; }
    .close-modal-btn { background: #f5f5f5; border: none; cursor: pointer; color: #333; width: 44px; height: 44px; border-radius: 50%; transition: all 0.2s; display: flex; align-items: center; justify-content: center; }
    .close-modal-btn:hover { background: #e5e5e5; }
    .modal-body { display: flex; flex: 1; overflow: hidden; background: #fff; max-width: 1600px; width: 100%; margin: 0 auto; }
    .category-sidebar { width: 260px; background: #fafafa; overflow-y: auto; border-right: 1px solid var(--border); padding: 24px; display: flex; flex-direction: column; gap: 8px; }
    .category-btn { display: block; width: 100%; text-align: left; padding: 14px 20px; border: none; background: transparent; border-radius: 4px; font-weight: 400; color: var(--text-sub); cursor: pointer; transition: all 0.2s; font-size: 1rem; }
    .category-btn:hover { background: #eee; color: var(--text-main); }
    .category-btn.active { background: var(--primary); color: #fff; font-weight: 400; box-shadow: 0 4px 12px rgba(47,125,94,0.2); }
    .menu-grid-container { flex: 1; overflow-y: auto; padding: 30px; background: #fff; }
    .dishes-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 24px; }
    .dish-card { border: 1px solid #eee; border-radius: 4px; padding: 16px; cursor: pointer; transition: all 0.2s; display: flex; flex-direction: column; height: 100%; background: #fff; box-sizing: border-box; }
    .dish-card:hover { border-color: var(--primary); transform: translateY(-4px); box-shadow: 0 10px 25px rgba(0,0,0,0.08); }
    .dish-img-wrap { position: relative; margin-bottom: 12px; }
    .dish-text { display: flex; flex-direction: column; flex: 1; min-width: 0; }
    .dish-image { width: 100%; height: 180px; background: #f0f0f0; border-radius: 4px; object-fit: cover; display: block; }
    .dish-image-placeholder { width: 100%; height: 180px; background: #f8f8f8; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #ccc; font-size: 0.9rem; border: 1px dashed #ddd; }
    .dish-name { font-weight: 400; margin-bottom: 4px; color: var(--text-main); font-size: 1.1rem; line-height: 1.3; }
    .dish-name-en { font-size: 0.9rem; color: #888; margin-bottom: 8px; }
    .dish-desc { font-size: 0.9rem; color: #666; margin-bottom: 16px; flex: 1; line-height: 1.5; display: -webkit-box; -webkit-line-clamp: 3; -webkit-box-orient: vertical; overflow: hidden; }
    .dish-footer { margin-top: auto; display: flex; justify-content: space-between; align-items: center; }
    .dish-price { font-weight: 400; color: var(--primary); font-size: 1.2rem; }
    .add-icon { position: absolute; bottom: 6px; right: 6px; width: 32px; height: 32px; background: rgba(255,255,255,0.95); border-radius: 50%; display: flex; align-items: center; justify-content: center; color: var(--primary); box-shadow: 0 2px 8px rgba(0,0,0,0.15); transition: all 0.2s; z-index: 1; }
    .dish-card:hover .add-icon { background: var(--primary); color: #fff; }
    .dish-card.sold-out { opacity: 0.6; pointer-events: none; position: relative; }
    .dish-card.sold-out::after { content: '售罄'; position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); background: rgba(0,0,0,0.7); color: white; padding: 8px 16px; border-radius: 4px; font-weight: 400; font-size: 1rem; z-index: 10; }
    .dish-card.sold-out .add-icon { display: none; }
    .modifier-view { display: flex; flex-direction: column; height: 100%; width: 100%; max-width: 900px; margin: 0 auto; }
    .mod-content { flex: 1; overflow-y: auto; padding: 40px; scrollbar-width: none; -ms-overflow-style: none; }
    .mod-content::-webkit-scrollbar { display: none; }
    .mod-dish-header { margin-bottom: 30px; padding-bottom: 24px; border-bottom: 1px solid #f0f0f0; }
    .mod-dish-image { width: 230px; height: 230px; object-fit: cover; border-radius: 4px; margin: 0 auto 16px; display: block; }
    .mod-dish-name { font-size: 1.8rem; font-weight: 400; color: var(--primary); margin-bottom: 6px; }
    .mod-dish-price { font-size: 1.2rem; color: var(--text-sub); font-weight: 400; margin-bottom: 8px; }
    .mod-dish-desc { font-size: 0.9rem; color: #888; line-height: 1.5; }
    .mod-group { margin-bottom: 30px; border: 1px solid #e8e8e8; border-radius: 4px; padding: 24px; background: #fff; }
    .mod-group-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 20px; padding-bottom: 12px; border-bottom: 1px dashed #eee; }
    .mod-title { font-weight: 400; font-size: 1.2rem; color: var(--text-main); display: flex; align-items: center; gap: 8px; }
    .mod-rules-badge { font-size: 0.8rem; padding: 6px 12px; border-radius: 20px; font-weight: 400; letter-spacing: 0.5px; }
    .badge-required { background: #fff0f0; color: var(--danger); border: 1px solid #fed7d7; }
    .badge-optional { background: #f0faf5; color: #2F7D5E; border: 1px solid #c8e6c9; }
    .mod-options-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: 16px; }
    .mod-option-card { border: 1px solid #f0f0f0; border-radius: 4px; padding: 16px; cursor: pointer; transition: all 0.2s; display: flex; justify-content: space-between; align-items: center; background: #fff; user-select: none; }
    .mod-option-card:hover { border-color: #d0d0d0; background: #fafafa; }
    .mod-option-card.selected { border-color: var(--primary); background: #fdfdfd; box-shadow: 0 4px 12px rgba(0,0,0,0.05); }
    .mod-opt-left { display: flex; flex-direction: column; gap: 2px; flex: 1; min-width: 0; }
    .mod-opt-name { font-weight: 400; color: var(--text-main); font-size: 1rem; }
    .mod-opt-price { font-size: 0.95rem; color: var(--text-sub); font-weight: 400; }
    .mod-option-card.selected .mod-opt-price { color: var(--primary); }
    .mod-opt-radio { width: 22px; height: 22px; border-radius: 50%; border: 1px solid #d0d0d0; display: flex; align-items: center; justify-content: center; flex-shrink: 0; transition: all 0.2s; }
    .mod-option-card:hover .mod-opt-radio { border-color: #aaa; }
    .mod-option-card.selected .mod-opt-radio { border-color: var(--primary); background: var(--primary); }
    .mod-opt-radio-inner { width: 8px; height: 8px; border-radius: 50%; background: #fff; opacity: 0; transform: scale(0); transition: all 0.2s; }
    .mod-option-card.selected .mod-opt-radio-inner { opacity: 1; transform: scale(1); }
    .mod-footer { padding: 24px 40px; border-top: 1px solid var(--border); display: flex; justify-content: space-between; align-items: center; background: #fff; box-shadow: 0 -4px 20px rgba(0,0,0,0.03); }
    .total-wrapper { display: flex; flex-direction: column; }
    .total-label { font-size: 0.9rem; color: #888; margin-bottom: 4px; }
    .total-amount { font-size: 1.6rem; font-weight: 400; color: var(--primary); }
    .confirm-btn { background: var(--primary); color: #fff; padding: 16px 48px; border-radius: 4px; border: none; font-weight: 400; font-size: 1.1rem; cursor: pointer; transition: all 0.2s; display: flex; align-items: center; gap: 10px; }
    .confirm-btn:hover { background: var(--primary-dark); transform: translateY(-1px); box-shadow: 0 4px 12px rgba(47,125,94,0.2); }
    .confirm-btn:active { transform: translateY(0); }

    @media (max-width: 900px) { .meal-selection-page { grid-template-columns: 1fr; max-width: 100vw; overflow-x: hidden; } .order-summary { position: static; order: 2; margin-top: 20px; } .slots-container { order: 1; } .slots-row { grid-template-columns: 1fr; } }
    @media (max-width: 768px) {
      .modal-header { flex-direction: column; gap: 8px; align-items: stretch; padding: 8px 16px; box-shadow: none; padding-bottom: 8px; margin-bottom: 0; }
      .modal-title-area { justify-content: center; position: relative; min-height: 40px; }
      .modal-title-area .close-modal-btn { position: absolute; left: 0; width: 36px; height: 36px; }
      .modal-title-area .back-btn { position: absolute; left: 0; }
      .modal-title { font-size: 1.1rem; }
      .search-bar-wrapper { max-width: 100%; }
      .search-input { padding: 8px 16px 8px 38px; font-size: 0.85rem; }
      .modal-body { flex-direction: column; overflow-x: hidden; }
      .modal-body { gap: 0; }
      .category-sidebar { width: 100%; height: auto; display: flex; flex-direction: row; overflow-x: auto; white-space: nowrap; padding: 6px 12px; border-right: none; border-bottom: 1px solid var(--border); box-sizing: border-box; background: #fff; margin: 0; }
      .category-btn { width: auto; margin-right: 6px; padding: 6px 14px; font-size: 0.85rem; }
      .menu-grid-container { padding: 0 !important; overflow-x: hidden; }
      .dishes-grid { grid-template-columns: 1fr; gap: 0; padding: 0; }
      .dish-card {
        flex-direction: row; gap: 12px; align-items: center;
        padding: 12px 16px; border-radius: 0; border: none; border-bottom: 1px solid #f0f0f0;
        box-sizing: border-box; overflow: hidden;
      }
      .dish-card:hover { transform: none; box-shadow: none; border-color: #f0f0f0; }
      .dish-text { order: 1; flex: 1; min-width: 0; }
      .dish-img-wrap { order: 2; flex-shrink: 0; margin-bottom: 0; }
      .dish-card.has-img .dish-img-wrap { width: 100px; height: 100px; }
      .dish-card.has-img .dish-image { width: 100%; height: 100%; border-radius: 4px; }
      .dish-card.no-img .dish-img-wrap { display: none; }
      .dish-image-placeholder { display: none; }
      .dish-name { font-size: 0.9rem; font-weight: 400; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .dish-name-en { font-size: 0.78rem; margin-bottom: 2px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .dish-desc { font-size: 0.78rem; margin-bottom: 4px; -webkit-line-clamp: 1; }
      .dish-footer { margin-top: 4px; }
      .dish-price { font-size: 0.95rem; }
      .add-icon { display: none; }
      .dish-card.sold-out::after { font-size: 0.85rem; padding: 4px 10px; }
      .mod-content { padding: 16px; }
      .mod-dish-header { text-align: center; }
      .mod-dish-image { width: 140px; height: 140px; }
      .mod-dish-name { font-size: 1.2rem; text-align: center; }
      .mod-dish-price { font-size: 1rem; text-align: center; }
      .mod-dish-desc { text-align: center; }
      .mod-group { border: none; border-radius: 0; padding: 0; margin-bottom: 24px; border-bottom: 1px solid #f0f0f0; padding-bottom: 16px; }
      .mod-group-header { margin-bottom: 0; padding-bottom: 10px; border-bottom: 1px solid #eee; }
      .mod-title { font-size: 1rem; }
      .mod-rules-badge { font-size: 0.72rem; padding: 4px 8px; }
      .mod-options-grid { display: flex; flex-direction: column; gap: 0; }
      .mod-option-card { border: none; border-radius: 0; padding: 14px 0; border-bottom: 1px solid #f5f5f5; }
      .mod-option-card:last-child { border-bottom: none; }
      .mod-option-card:hover { background: transparent; }
      .mod-option-card.selected { background: transparent; box-shadow: none; border-bottom: none; }
      .mod-opt-name { font-size: 0.9rem; }
      .mod-opt-price { font-size: 0.85rem; }
      .mod-footer { padding: 16px; }
      .confirm-btn { padding: 12px 24px; font-size: 1rem; }
    }
  `}</style>
);

const MealSelectionInner = () => {
  const { t, i18n } = useTranslation('booking');
  const isZh = i18n.language?.startsWith('zh');
  const navigate = useNavigate();
  const {
    next7Days, selectedDays, mealSelections,
    menuOptions, categoriesList, modifierGroupsList,
    bookingMode, selectMealForDay, removeDay, handleMealSelectionNext,
    matchedZone
  } = useOutletContext();

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [activeSlot, setActiveSlot] = useState(null);
  const [viewMode, setViewMode] = useState('menu');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategoryId, setSelectedCategoryId] = useState('');
  const [pendingDish, setPendingDish] = useState(null);
  const [selectedModifiers, setSelectedModifiers] = useState({});

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (isModalOpen) { document.body.style.overflow = 'hidden'; } else { document.body.style.overflow = 'unset'; }
    return () => { document.body.style.overflow = 'unset'; };
  }, [isModalOpen]);

  const sidebarCategories = useMemo(() => categoriesList || [], [categoriesList]);

  const filteredMenu = useMemo(() => {
    let items = menuOptions || [];
    if (selectedCategoryId) items = items.filter(item => Array.isArray(item.categories) && item.categories.includes(selectedCategoryId));
    if (searchTerm) {
      const lower = searchTerm.toLowerCase();
      items = items.filter(m => (m.name?.toLowerCase().includes(lower)) || (m.description?.toLowerCase().includes(lower)) || (m.alternate_name?.toLowerCase().includes(lower)));
    }
    return items;
  }, [menuOptions, selectedCategoryId, searchTerm]);

  const getSelectedMealData = (dayId, type) => {
    const selection = mealSelections[dayId]?.[type];
    if (!selection || !selection.id) return null;
    const meal = menuOptions.find(m => m.id === selection.id);
    if (!meal) return null;
    const allCurrentMods = modifierGroupsList?.flatMap(g => g.modifiers || []) || [];
    const updatedMods = (selection.modifiers || []).map(mod => {
      const current = allCurrentMods.find(m => m.id === mod.id);
      return current ? { ...mod, name: current.name } : mod;
    });
    return { ...meal, selectedModifiers: updatedMods };
  };

  const orderTotals = useMemo(() => {
    let subtotal = 0;
    let gst = 0;
    let pst = 0;

    selectedDays.forEach(dayId => {
      ['lunch', 'dinner'].forEach(type => {
        const meal = getSelectedMealData(dayId, type);
        if (meal) {
          let itemPrice = meal.price || 0;
          if (Array.isArray(meal.selectedModifiers)) {
            meal.selectedModifiers.forEach(mod => { itemPrice += (mod.price || 0); });
          }
          subtotal += itemPrice;
          if (meal.tax_gst) gst += itemPrice * 0.05;
          if (meal.tax_pst) pst += itemPrice * 0.07;
        }
      });
    });

    // 計算總餐數：3餐以上免運費
    let totalMealCount = 0;
    selectedDays.forEach(dayId => {
      const s = mealSelections[dayId];
      if (s?.lunch) totalMealCount++;
      if (s?.dinner) totalMealCount++;
    });

    let rawFee = 0;
    if (matchedZone && totalMealCount < 3) {
        if (matchedZone.delivery_fee !== undefined && matchedZone.delivery_fee !== null) rawFee = matchedZone.delivery_fee;
        else if (matchedZone.deliveryFee !== undefined && matchedZone.deliveryFee !== null) rawFee = matchedZone.deliveryFee;
    }
    const deliveryFee = Number(rawFee);
    const freeDelivery = totalMealCount >= 3;
    const total = subtotal + gst + pst + deliveryFee;

    return {
      subtotal: subtotal.toFixed(2),
      gst: gst.toFixed(2),
      pst: pst.toFixed(2),
      deliveryFee: deliveryFee.toFixed(2),
      deliveryFeeVal: deliveryFee,
      freeDelivery,
      totalMealCount,
      grandTotal: total.toFixed(2)
    };
  }, [selectedDays, mealSelections, menuOptions, matchedZone]);

  const openMenuForSlot = (dayId, type) => {
    setActiveSlot({ dayId, type });
    setSearchTerm('');
    if (categoriesList && categoriesList.length > 0) setSelectedCategoryId(categoriesList[0].id); else setSelectedCategoryId('');
    setViewMode('menu');
    setPendingDish(null);
    setSelectedModifiers({});
    setIsModalOpen(true);
  };

  const handleDishClick = (dish) => {
    const hasModifiers = dish.modifier_groups && dish.modifier_groups.length > 0;
    if (hasModifiers) {
      setPendingDish(dish);
      setSelectedModifiers({});
      setViewMode('modifiers');
    } else {
      selectMealForDay(activeSlot.dayId, { id: dish.id, modifiers: [] }, activeSlot.type);
      setIsModalOpen(false);
    }
  };

  const toggleModifier = (group, mod) => {
    const currentSelection = selectedModifiers[group.id] || [];
    const isSelected = currentSelection.some(m => m.id === mod.id);
    let newSelection;
    if (isSelected) {
      // 已選中，點擊取消
      newSelection = currentSelection.filter(m => m.id !== mod.id);
    } else if (group.max_allowed === 1) {
      // 單選模式，替換選擇
      newSelection = [mod];
    } else {
      // 多選模式，新增選擇
      if (currentSelection.length >= group.max_allowed) { return; }
      newSelection = [...currentSelection, mod];
    }
    setSelectedModifiers({ ...selectedModifiers, [group.id]: newSelection });
  };

  const handleConfirmModifiers = () => {
    const groups = getDishModifierGroups(pendingDish);
    for (const group of groups) {
       const selected = selectedModifiers[group.id] || [];
       if (selected.length < group.min_required) { alert(t('meal_selection.alert_min_modifier', { name: group.name, min: group.min_required })); return; }
    }
    const allMods = Object.values(selectedModifiers).flat();
    selectMealForDay(activeSlot.dayId, { id: pendingDish.id, modifiers: allMods }, activeSlot.type);
    setIsModalOpen(false);
  };

  const handleClearMeal = (e, dayId, type) => {
    e.stopPropagation();
    if(window.confirm(t('meal_selection.confirm_remove'))) { selectMealForDay(dayId, null, type); }
  };

  const getDishModifierGroups = (dish) => {
    if (!dish || !dish.modifier_groups || !modifierGroupsList) return [];
    return modifierGroupsList
      .filter(g => dish.modifier_groups.includes(g.id))
      .sort((a, b) => (a.order || 999) - (b.order || 999));
  };

  const calculatePreviewTotal = () => {
    if (!pendingDish) return 0;
    let total = pendingDish.price;
    Object.values(selectedModifiers).flat().forEach(m => total += (m.price || 0));
    return total.toFixed(2);
  };

  const isReadyToProceed = () => {
    if (selectedDays.length === 0) return false;
    return selectedDays.every(dayId => { const sel = mealSelections[dayId]; return sel && (sel.lunch || sel.dinner); });
  };

  return (
    <>
      <MealSelectionStyles />
      <StepIndicator currentStep={bookingMode === 'single' ? 1 : 2} bookingMode={bookingMode} />

      <div className="meal-selection-page">
        {/* 左側 Slots 列表 */}
        <div className="slots-container">
           {selectedDays.map(dayId => {
             const day = next7Days.find(d => d.id === dayId);
             const lunch = getSelectedMealData(dayId, 'lunch');
             const dinner = getSelectedMealData(dayId, 'dinner');
             return (
               <div key={dayId} className="date-slot-card">
                 <div className="date-header">
                   <div className="date-title"><CalendarDays size={20} />{day.fullDate} ({day.weekday})</div>
                 </div>
                 <div className="slots-row">
                   {['lunch', 'dinner'].map(type => {
                     const item = type === 'lunch' ? lunch : dinner;
                     const label = type === 'lunch' ? t('meal_selection.lunch') : t('meal_selection.dinner');
                     const tagClass = type === 'lunch' ? 'tag-lunch' : 'tag-dinner';
                     const isFilled = !!item;
                     return (
                       <button key={type} className={`meal-slot-btn ${isFilled ? 'filled' : 'empty'}`} onClick={() => openMenuForSlot(dayId, type)}>
                         {isFilled ? (
                           <div className="slot-filled-wrapper">
                             {item.image_url ? (<img src={item.image_url} alt={item.name} className="slot-thumbnail" loading="lazy" decoding="async" />) : (<div className="slot-thumbnail-placeholder"><ImageIcon size={24} /></div>)}
                             <div className="slot-content-area">
                               <span className={`slot-tag ${tagClass}`}>{label}</span>
                               <div className="selected-dish-name">{item.name}</div>
                               {item.selectedModifiers.length > 0 && (<div className="selected-dish-mods">+ {item.selectedModifiers.map(m => m.name).join(', ')}</div>)}
                             </div>
                             <div className="slot-actions">
                               <div className="action-btn" title={t('meal_selection.change_meal')}><Edit3 size={15} /></div>
                               <div className="action-btn delete" title={t('meal_selection.remove_meal')} onClick={(e) => handleClearMeal(e, dayId, type)}><Trash2 size={15} /></div>
                             </div>
                           </div>
                         ) : (
                           <div className="add-meal-action"><div className="add-icon-circle"><PlusCircle size={20} /></div><span>{type === 'lunch' ? t('meal_selection.add_lunch') : t('meal_selection.add_dinner')}</span></div>
                         )}
                       </button>
                     )
                   })}
                 </div>
               </div>
             );
           })}
        </div>

        {/* ================= 右側：專業版訂單摘要 (Professional Receipt Style) ================= */}
        <div className="order-summary">
          <div className="summary-header">
            <ShoppingCart size={22} />
            {t('meal_selection.order_summary')}
          </div>

          <div className="summary-scroll">
             {selectedDays.length === 0 && (<div style={{textAlign:'center', padding:'30px 0', color:'#999'}}>{t('meal_selection.no_dates')}</div>)}

             {selectedDays.map(dayId => {
                const day = next7Days.find(d => d.id === dayId);
                const sel = mealSelections[dayId];
                if(!sel) return null;
                const lunch = sel.lunch && getSelectedMealData(dayId, 'lunch');
                const dinner = sel.dinner && getSelectedMealData(dayId, 'dinner');
                if(!lunch && !dinner) return null;

                return (
                   <div key={dayId} className="summary-day-block">
                      <div className="summary-date-label">{day.fullDate} ({day.weekday})</div>

                      {/* 午餐區塊 */}
                      {lunch && (
                          <div className="summary-item-row">
                             {/* 1. 圖片 (Column 1) */}
                             {lunch.image_url ? <img src={lunch.image_url} alt="lunch" className="summary-img" loading="lazy" decoding="async" /> : <div className="summary-img" style={{display:'flex',alignItems:'center',justifyContent:'center'}}><ImageIcon size={20} color="#ccc"/></div>}

                             {/* 2. 內容列 (Column 2 - 包含所有資訊) */}
                             <div className="summary-info-col">
                                <span className="summary-tag-label label-lunch">{t('meal_selection.lunch')}</span>

                                {/* 標題與主價格放在同一行 Flex 容器中 */}
                                <div className="dish-title-row">
                                    <div className="summary-dish-title">{lunch.name}</div>
                                    <div className="summary-dish-price">${lunch.price.toFixed(2)}</div>
                                </div>

                                {/* 配料列表 */}
                                {lunch.selectedModifiers?.length > 0 && (
                                    <div className="summary-mods-container">
                                        {lunch.selectedModifiers.map(mod => (
                                            <div key={mod.id} className="mod-line">
                                                <span className="mod-name">+ {mod.name}</span>
                                                <span className="mod-price">{mod.price > 0 ? `+$${mod.price.toFixed(2)}` : ''}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                             </div>
                          </div>
                      )}

                      {/* 晚餐區塊 */}
                      {dinner && (
                          <div className="summary-item-row">
                             {/* 1. 圖片 */}
                             {dinner.image_url ? <img src={dinner.image_url} alt="dinner" className="summary-img" loading="lazy" decoding="async" /> : <div className="summary-img" style={{display:'flex',alignItems:'center',justifyContent:'center'}}><ImageIcon size={20} color="#ccc"/></div>}

                             {/* 2. 內容列 */}
                             <div className="summary-info-col">
                                <span className="summary-tag-label label-dinner">{t('meal_selection.dinner')}</span>

                                {/* 標題與主價格放在同一行 Flex 容器中 */}
                                <div className="dish-title-row">
                                    <div className="summary-dish-title">{dinner.name}</div>
                                    <div className="summary-dish-price">${dinner.price.toFixed(2)}</div>
                                </div>

                                {dinner.selectedModifiers?.length > 0 && (
                                    <div className="summary-mods-container">
                                        {dinner.selectedModifiers.map(mod => (
                                            <div key={mod.id} className="mod-line">
                                                <span className="mod-name">+ {mod.name}</span>
                                                <span className="mod-price">{mod.price > 0 ? `+$${mod.price.toFixed(2)}` : ''}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                             </div>
                          </div>
                      )}
                   </div>
                )
             })}
          </div>

          <div className="summary-total-section">
            <div className="bill-row">
                <span>{t('meal_selection.subtotal')}</span>
                <span>${orderTotals.subtotal}</span>
            </div>

            {/* 運費 (無 Icon, 無粗體) */}
            <div className="bill-row fee-row">
                <span>{t('meal_selection.delivery_fee')}</span>
                <span style={{color: orderTotals.deliveryFeeVal === 0 ? '#166534' : 'inherit'}}>
                    {orderTotals.deliveryFeeVal === 0 ? t('meal_selection.free') : `$${orderTotals.deliveryFee}`}
                </span>
            </div>
            {!orderTotals.freeDelivery && orderTotals.deliveryFeeVal > 0 && (
              <div className="bill-row fee-row">
                <span style={{color: '#2F7D5E'}}>滿 3 餐即享免運費</span>
                <span style={{color: '#2F7D5E'}}>還差 {3 - orderTotals.totalMealCount} 餐</span>
              </div>
            )}

            <div className="bill-row tax-row">
                <span>GST (5%)</span>
                <span>${orderTotals.gst}</span>
            </div>

            <div className="bill-row tax-row">
                <span>PST (7%)</span>
                <span>${orderTotals.pst}</span>
            </div>

            <div className="bill-total">
               <span className="total-label">{t('meal_selection.total_label')}</span>
               <span className="total-value">CAD$ {orderTotals.grandTotal}</span>
            </div>

            <div className="summary-actions">
              <button className="next-step-btn" onClick={handleMealSelectionNext} disabled={!isReadyToProceed()}>
                {t('meal_selection.next')} <ChevronRight size={18} />
              </button>
              <button className="prev-step-btn" onClick={() => navigate('../')}>
                <ArrowLeft size={16} style={{marginRight: 6}}/> {t('meal_selection.back_dates')}
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Modal 保持不變 */}
      {isModalOpen && (
          <div className="menu-modal-overlay">
            <div className="menu-modal">
               <div className="modal-header">
                  <div className="modal-title-area">
                    {viewMode === 'modifiers' ? (
                      <button className="back-btn" onClick={() => setViewMode('menu')}><ArrowLeft size={20}/> {t('meal_selection.back_menu')}</button>
                    ) : (
                      <>
                        <button className="close-modal-btn" onClick={() => setIsModalOpen(false)}><X size={24} /></button>
                        <div className="modal-title">{t('meal_selection.select_type', { type: activeSlot.type === 'lunch' ? t('meal_selection.lunch') : t('meal_selection.dinner') })}</div>
                      </>
                    )}
                  </div>
                  {viewMode === 'menu' && (<div className="search-bar-wrapper"><Search size={18} className="search-icon" /><input type="text" className="search-input" placeholder={t('meal_selection.search_placeholder')} value={searchTerm} onChange={e => setSearchTerm(e.target.value)} /></div>)}
               </div>
               {viewMode === 'menu' ? (
                  <div className="modal-body">
                      <div className="category-sidebar">
                         {sidebarCategories.map(cat => (<button key={cat.id} className={`category-btn ${selectedCategoryId === cat.id ? 'active' : ''}`} onClick={() => setSelectedCategoryId(cat.id)}>{cat.name}</button>))}
                      </div>
                      <div className="menu-grid-container">
                         {filteredMenu.length === 0 ? (<div style={{textAlign:'center', padding:'40px', color:'#999'}}>{t('meal_selection.no_results')}</div>) : (
                           <div className="dishes-grid">
                              {filteredMenu.map(dish => (
                                 <div key={dish.id} className={`dish-card ${dish.image_url ? 'has-img' : 'no-img'} ${dish.is_available === false ? 'sold-out' : ''}`} onClick={() => dish.is_available !== false && handleDishClick(dish)}>
                                    <div className="dish-img-wrap">
                                      {dish.image_url ? (<img src={dish.image_url} alt={dish.name} className="dish-image" loading="lazy" decoding="async" />) : (<div className="dish-image-placeholder">{t('meal_selection.no_image')}</div>)}
                                      <div className="add-icon"><PlusCircle size={20} /></div>
                                    </div>
                                    <div className="dish-text">
                                      <div className="dish-name">{isZh ? dish.name : (dish.alternate_name || dish.name)}</div>
                                      {!isZh && dish.name !== dish.alternate_name && dish.name && (<div className="dish-name-en">{dish.name}</div>)}
                                      {dish.description && <div className="dish-desc">{dish.description}</div>}
                                      <div className="dish-footer"><div className="dish-price">CAD$ {dish.price}</div></div>
                                    </div>
                                 </div>
                              ))}
                           </div>
                         )}
                      </div>
                  </div>
               ) : (
                  <div className="modal-body" style={{justifyContent: 'center'}}>
                      <div className="modifier-view">
                         <div className="mod-content">
                            <div className="mod-dish-header">{pendingDish?.image_url && <img src={pendingDish.image_url} alt={pendingDish.name} className="mod-dish-image" loading="lazy" decoding="async" />}<div className="mod-dish-name">{pendingDish?.name}</div><div className="mod-dish-price">CAD$ {pendingDish?.price}</div>{pendingDish?.description && <div className="mod-dish-desc">{pendingDish.description}</div>}</div>
                            {getDishModifierGroups(pendingDish).length === 0 && (<div style={{padding:'20px', color:'#666'}}>{t('meal_selection.no_modifiers_hint')}</div>)}
                            {getDishModifierGroups(pendingDish).map(group => (
                              <div key={group.id} className="mod-group">
                                 <div className="mod-group-header"><span className="mod-title">{group.name}</span><span className={`mod-rules-badge ${group.min_required > 0 ? 'badge-required' : 'badge-optional'}`}>{group.min_required > 0 ? t('meal_selection.required') : t('meal_selection.optional')}{` (${t('meal_selection.max_select', { max: group.max_allowed })})`}</span></div>
                                 <div className="mod-options-grid">
                                    {group.modifiers.map(mod => {
                                       const isSelected = (selectedModifiers[group.id] || []).some(m => m.id === mod.id);
                                       return (<div key={mod.id} className={`mod-option-card ${isSelected ? 'selected' : ''}`} onClick={() => toggleModifier(group, mod)}><div className="mod-opt-left"><span className="mod-opt-name">{mod.name}</span><span className="mod-opt-price">{mod.price > 0 ? `+$${mod.price}` : t('meal_selection.free')}</span></div><div className="mod-opt-radio"><div className="mod-opt-radio-inner"></div></div></div>)
                                    })}
                                 </div>
                              </div>
                            ))}
                         </div>
                         <div className="mod-footer">
                            <div className="total-wrapper"><span className="total-label">{t('meal_selection.estimated')}</span><span className="total-amount">CAD$ {calculatePreviewTotal()}</span></div>
                            <button className="confirm-btn" onClick={handleConfirmModifiers}>{t('meal_selection.confirm_add')} <Check size={20} /></button>
                         </div>
                      </div>
                  </div>
               )}
            </div>
          </div>
      )}
    </>
  );
};

const MealSelection = () => {
  const { bookingMode } = useOutletContext();
  if (bookingMode === 'single') return <SingleMealSelection />;
  return <MealSelectionInner />;
};

export default MealSelection;